import {useAsyncState} from 'common/hooks/useAsyncState';
import ApiService from 'api/ApiService';
import React from 'react';
import {useHandleApiError} from './useHandleApiError';
import {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useToasts} from 'features/notifications/useToasts';

export type CallbackOptions<ResponseType, K = ResponseType> = {
   onSuccess?: (data: K) => void;
   onFailure?: (error: any, message: string) => void;
   transformData?: (data: ResponseType) => K;
};

type ApiOptions<ResponseType, K = ResponseType> = CallbackOptions<ResponseType, K> & {
   persistState?: boolean;
   hideToast?: boolean;
   successMessage?: string;
   updateValues?: boolean;
};

export type RequestOptions<ResponseType, K = ResponseType> = CallbackOptions<ResponseType, K> & {
   urlParams?: string;
   searchParams?: Record<string, string>;
   helpers?: FormOnSubmitHelpers<any>;
};

export function usePostApi<ResponseType = any, K = ResponseType>(
   url: string,
   options?: ApiOptions<ResponseType, K>
) {
   const {state, loading, error, setState, setLoading, setError} = useAsyncState<K>();
   const {addToast} = useToasts();

   const handleError = useHandleApiError({
      url,
      setError,
      setLoading,
      hideToast: options?.hideToast,
   });

   const request = React.useCallback(
      (body: BodyInit | null, requestOptions?: RequestOptions<ResponseType, K>) => {
         let _url = url;
         if (requestOptions?.urlParams) {
            _url = url + requestOptions.urlParams;
         }
         if (requestOptions?.searchParams) {
            _url = `${_url}?${new URLSearchParams(requestOptions?.searchParams)}`;
         }
         setLoading(true);
         !options?.persistState && setState(undefined);
         return ApiService.postSecureJson(_url, body)
            .then((res) => {
               return res.json().catch(() => res);
            })
            .then((_data: ResponseType) => {
               const data = options?.transformData ? options.transformData(_data) : _data;

               setState(data as K);
               setLoading(false);
               options?.onSuccess?.(data as K);
               requestOptions?.onSuccess?.(data as K);
               options?.successMessage &&
                  addToast(options?.successMessage, {appearance: 'success'});
               options?.updateValues && requestOptions?.helpers?.resetForm(data);
               return data;
            })
            .catch(handleError(options?.onFailure || requestOptions?.onFailure))
            .finally(() => {
               requestOptions?.helpers?.setSubmitting(false);
            });
      },
      [handleError, options, setLoading, setState, url, addToast]
   );
   return {request, state, loading, error};
}
